/**
 * SupaAuthContext — Supabase-Auth-backed session state for the B2B
 * surface (/admin and /firm).  Deliberately isolated from the consumer
 * app's cookie-based AuthContext.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  apiV2,
  getSupaToken,
  getSupaUser,
  setSupaSession,
  clearSupaSession,
} from "@/lib/apiV2";

const SupaAuthContext = createContext(null);

export function SupaAuthProvider({ children }) {
  const [user, setUser] = useState(() => getSupaUser());
  const [token, setToken] = useState(() => getSupaToken());
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!getSupaToken()) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await apiV2.get("/me");
      setUser({ id: data.user_id, email: data.email });
      setProfile(data.profile || null);
      setSupaSession(getSupaToken(), { id: data.user_id, email: data.email });
    } catch {
      // 401 interceptor already cleared the session.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await apiV2.post("/auth/login", { email, password });
    if (!data?.access_token) throw new Error("Login response missing token");
    setSupaSession(data.access_token, data.user);
    setToken(data.access_token);
    setUser(data.user);
    await refresh();
    return data.user;
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await apiV2.post("/auth/logout");
    } catch { /* ignore */ }
    clearSupaSession();
    setUser(null);
    setToken(null);
    setProfile(null);
  }, []);

  const value = useMemo(() => ({
    user, token, profile, loading, login, logout, refresh,
    isAuthed: !!user && !!token,
  }), [user, token, profile, loading, login, logout, refresh]);

  return <SupaAuthContext.Provider value={value}>{children}</SupaAuthContext.Provider>;
}

export function useSupaAuth() {
  const ctx = useContext(SupaAuthContext);
  if (!ctx) throw new Error("useSupaAuth must be used inside <SupaAuthProvider>");
  return ctx;
}
